import { SkyBadge, SkyButton } from "@skypointcloud/skypoint-ui";
import type { TenantResourcesView } from "@shared/types/tenantResources";

/**
 * A tenant's Resources chip (ADR 0010, design §7.5): one word and one colour, never a colour alone.
 * A tenant on the deployment's shared account reads "Shared"; a tenant with its own Azure resources
 * reads its status. The chip opens the drawer that shows the two steppers.
 */

export type ResourcesChipState = "Provisioning" | "Active" | "Failed" | "Shared";

export function resourcesChipState(resources: TenantResourcesView): ResourcesChipState {
  if (resources.mode === "shared") return "Shared";
  if (resources.status === "provisioning") return "Provisioning";
  return resources.status === "failed" ? "Failed" : "Active";
}

export const CHIP_COLOR: Record<
  ResourcesChipState,
  "informative" | "success" | "danger" | "subtle"
> = {
  Provisioning: "informative",
  Active: "success",
  Failed: "danger",
  Shared: "subtle",
};

export function TenantResourcesChip({
  tenantId,
  tenantName,
  resources,
  onOpen,
}: {
  tenantId: string;
  tenantName: string;
  resources: TenantResourcesView;
  onOpen: () => void;
}) {
  const state = resourcesChipState(resources);
  return (
    <SkyButton
      appearance="subtle"
      size="small"
      onClick={onOpen}
      aria-label={`Azure resources of ${tenantName}: ${state}. Open details`}
      testId={`tenant-resources-${tenantId}`}
    >
      <SkyBadge
        appearance="tint"
        color={CHIP_COLOR[state]}
        testId={`tenant-resources-chip-${tenantId}`}
      >
        {state}
      </SkyBadge>
    </SkyButton>
  );
}
